import { Filter } from '@loopback/filter';
import { api } from 'api/api';
import {
  AreaCount,
  ConfigCount,
  StatCount,
  Statistics,
  UserConnectionCount,
} from 'shared/model/statistic.model';
import {
  ConfigContent,
  ConnectionContent,
  CriteriaContent,
  UsageLog,
} from 'shared/model/usageLog.model';

export const extendedApi = api
  .enhanceEndpoints({
    addTagTypes: ['usage-logs', 'user-usage-logs'],
  })
  .injectEndpoints({
    overrideExisting: true,
    endpoints: builder => ({
      getUsageLogs: builder.query<UsageLog[], Filter<UsageLog> | void>({
        query: filter => ({
          url: `/usage-logs`,
          method: 'GET',
          params: filter
            ? {
                filter: JSON.stringify(filter),
              }
            : undefined,
        }),
        providesTags: ['usage-logs'],
      }),
      getStatistics: builder.query<Statistics, Filter<UsageLog> | void>({
        query: filter => ({
          url: `/usage-logs`,
          method: 'GET',
          params: filter
            ? {
                filter: JSON.stringify(filter),
              }
            : undefined,
        }),
        transformResponse: (response: UsageLog[]) => {
          const statistics: Statistics = {
            topAreas: [],
            topConfigs: [],
            usersConnections: [],
            topRevisitTimes: [],
            avgRevistTime: 0,
          };

          const areaCounts: AreaCount[] = [];
          const configCounts: ConfigCount[] = [];
          const connectionCounts: UserConnectionCount[] = [];
          const revisitsCount: StatCount[] = [];

          let revisitTimeSum = 0;
          let criteriaSum = 0;

          response.forEach(log => {
            if (log.type === 'CRITERIA') {
              const content = log.content as CriteriaContent;
              const areaId = content.areaId;
              const areaCount = areaCounts.find(ac => ac.areaId === areaId);
              const revistCount = revisitsCount.find(rc => rc.name === content.revisitTime + '');

              if (revistCount) {
                revistCount.count++;
              } else {
                revisitsCount.push({ name: content.revisitTime + '', count: 1 });
              }

              revisitTimeSum += content.revisitTime;
              criteriaSum++;

              if (areaCount) {
                areaCount.count++;
              } else {
                areaCounts.push({ areaId: content.areaId, count: 1 });
              }
            } else if (log.type === 'CONFIG_SELECTED') {
              const content = log.content as ConfigContent;
              const resultId = content.resultId;
              const configCount = configCounts.find(cc => cc.resultId === resultId);
              if (configCount) {
                configCount.count++;
              } else {
                configCounts.push({ resultId: content.resultId, count: 1 });
              }
            } else if (log.type === 'CONNECTION') {
              const content = log.content as ConnectionContent;
              const userId = content.userId;
              const connectionCount = connectionCounts.find(cc => cc.userId === userId);

              if (connectionCount) {
                connectionCount.connectionNb += 1;
                connectionCount.timespent += content.timespent;
              } else {
                connectionCounts.push({
                  userId,
                  connectionNb: 1,
                  timespent: content.timespent,
                });
              }
            }
          });

          statistics.topAreas = areaCounts.sort((a, b) => b.count - a.count).slice(0, 5);
          statistics.topConfigs = configCounts.sort((a, b) => b.count - a.count).slice(0, 5);
          statistics.topRevisitTimes = revisitsCount.sort((a, b) => b.count - a.count).slice(0, 5);
          statistics.avgRevistTime = revisitTimeSum / criteriaSum;
          statistics.usersConnections = connectionCounts.sort(
            (a, b) => b.connectionNb - a.connectionNb,
          );

          return statistics;
        },
        providesTags: ['usage-logs'],
      }),
      getUserStatistics: builder.query<UsageLog[], Filter<UsageLog>>({
        query: filter => ({
          url: `/usage-logs`,
          method: 'GET',
          params: filter
            ? {
                filter: JSON.stringify(filter),
              }
            : undefined,
        }),
        providesTags: ['user-usage-logs'],
      }),
    }),
  });

export const { useGetUsageLogsQuery, useGetStatisticsQuery, useGetUserStatisticsQuery } =
  extendedApi;
